import { computed, ComputedRef, Ref, ref } from "vue"

type ValidationWatcher<F> = (rules: Rule<F>[]) => boolean

export type Rule<F, K extends keyof F = keyof F> = {
  field: K
  message: string
  selector: string
  panelIdentifier: any
  action: (v: F[K]) => boolean
}

export type FormProperty<F> = {
  name: keyof F
}

export default abstract class FormService<F> {
  abstract _form: ComputedRef<any>

  abstract _updateForm(key: string, value: any): void

  getFormValue<K extends keyof F>(key: K): ComputedRef<F[K]> {
    return computed(() => this._form.value[key])
  }

  setFormValue<K extends keyof F>(key: K, value: F[K]): void {
    this._updateForm(key as string, value)
  }

  private _rules: Rule<F>[] = []

  registerRule(rule: Rule<F>) {
    this._rules.push(rule)
  }

  validate(): boolean {
    const failedRules = this._rules.filter(
      (rule) => !rule.action(this._form.value[rule.field])
    )
    if (this._validationWatcher) {
      this._validationWatcher(failedRules)
    }
    return !failedRules.length
  }

  private _validationWatcher: ValidationWatcher<F>

  registerValidationWatcher(validator: ValidationWatcher<F>) {
    this._validationWatcher = validator
  }

  isEdit: ComputedRef<boolean> = computed(() => !!this._form.value?.id)

  formError: Ref<string> = ref(null)

  setFormError(error: string) {
    this.formError.value = error
  }

  private _properties: FormProperty<F>[] = []

  registerProperty(property: FormProperty<F>) {
    this._properties.push(property)
  }

  getFormValues(): any {
    const values = {}
    this._properties.forEach((property) => {
      values[property.name as string] = this.getFormValue(property.name).value
    })
    return values
  }
}
